import TagDTO, { ControlTagDTO, ProjectTagDTO, RiskTagDTO, TaskTagDTO, ThemeTagDTO } from 'models/dto/tagDTO';
import Tag from 'models/tag';
import AppError from 'utils/appError';
import http from './httpService';
import { mapFromEntities } from 'mappings/activityMapping';
import { mapFromTag, mapFromTags } from 'mappings/tagMapping';
import EntityDTO from 'models/dto/entityDTO';
import IdListDTO from 'models/dto/IdListDTO';
import { ObjectiveTagDTO } from 'models/dto/Objective/objectiveTagDTO';
import { ProcessTagDTO } from 'models/dto/Process/processTagDTO';
import Entity from 'models/entity';

export async function apiGetTags(includeUsedCount: boolean, accessToken: string): Promise<Tag[]> {
  try {
    const res = await http.get<TagDTO[]>(
      `/tags?includeUsedCount=${includeUsedCount}`,
      http.getRequestConfig(accessToken),
    );
    if (!res?.data) return [];

    return mapFromTags(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTagsForTenants(tenants: string[], accessToken: string): Promise<Tag[]> {
  try {
    const res = await http.post<TagDTO[]>(`/tags/tenants`, new IdListDTO(tenants), http.getRequestConfig(accessToken));
    if (!res?.data) return [];

    return mapFromTags(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateTag(tag: TagDTO, accessToken: string): Promise<Tag> {
  try {
    const res = await http.post<TagDTO>(`/tags`, tag, http.getRequestConfig(accessToken));

    return mapFromTag(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteTag(tag: Tag, accessToken: string): Promise<void> {
  try {
    await http.post(`/tags/delete/${tag.tagId}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTag(tag: TagDTO, accessToken: string): Promise<Tag> {
  try {
    const res = await http.put<TagDTO>(`/tags`, tag, http.getRequestConfig(accessToken));

    return mapFromTag(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Task
export async function apiAddTagToTask(tag: Tag, taskId: number, accessToken: string): Promise<void> {
  try {
    const body = new TaskTagDTO();
    body.tagId = tag.tagId;
    body.taskId = taskId;
    await http.post<TagDTO>(`/tags/task?add=${true}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveTagFromTask(tagId: number, taskId: number, accessToken: string): Promise<void> {
  try {
    const body = new TaskTagDTO();
    body.tagId = tagId;
    body.taskId = taskId;
    await http.post<TagDTO>(`/tags/task?add=${false}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Theme
export async function apiRemoveTagFromTheme(tag: Tag, themeId: number, accessToken: string): Promise<void> {
  try {
    const body = new ThemeTagDTO();
    body.tagId = tag.tagId;
    body.themeId = themeId;
    await http.post(`/tags/theme?add=${false}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddTagToTheme(tag: Tag, themeId: number, accessToken: string): Promise<void> {
  try {
    const body = new ThemeTagDTO();
    body.tagId = tag.tagId;
    body.themeId = themeId;
    await http.post<TagDTO>(`/tags/theme?add=${true}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTagUsage(tagId: number, accessToken: string): Promise<Entity[]> {
  try {
    const res = await http.get<EntityDTO[]>(`/tags/${tagId}/usage`, http.getRequestConfig(accessToken));
    if (!res?.data) return [];

    return mapFromEntities(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Risks
export async function apiAddTagToRisk(tag: Tag, riskId: number, accessToken: string): Promise<void> {
  try {
    const body = new RiskTagDTO();
    body.tagId = tag.tagId;
    body.riskId = riskId;
    await http.post<TagDTO>(`/tags/risk?add=${true}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveTagFromRisk(tag: Tag, riskId: number, accessToken: string): Promise<void> {
  try {
    const body = new RiskTagDTO();
    body.tagId = tag.tagId;
    body.riskId = riskId;
    await http.post<TagDTO>(`/tags/risk?add=${false}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Projects - tags on a project are display and grouping only; nothing is gated on them (R4).
export async function apiAddTagToProject(tag: Tag, projectId: number, accessToken: string): Promise<void> {
  try {
    const body = new ProjectTagDTO();
    body.tagId = tag.tagId;
    body.projectId = projectId;
    await http.post<TagDTO>(`/tags/project?add=${true}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveTagFromProject(tag: Tag, projectId: number, accessToken: string): Promise<void> {
  try {
    const body = new ProjectTagDTO();
    body.tagId = tag.tagId;
    body.projectId = projectId;
    await http.post<TagDTO>(`/tags/project?add=${false}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Controls
export async function apiRemoveTagFromControl(tag: Tag, controlId: number, accessToken: string): Promise<void> {
  try {
    const body = new ControlTagDTO();
    body.tagId = tag.tagId;
    body.controlId = controlId;
    await http.post(`/tags/control?add=${false}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddTagToControl(tag: Tag, controlId: number, accessToken: string): Promise<void> {
  try {
    const body = new ControlTagDTO();
    body.tagId = tag.tagId;
    body.controlId = controlId;
    await http.post<TagDTO>(`/tags/control?add=${true}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Objectives
export async function apiRemoveTagFromObjective(tag: Tag, objectiveId: number, accessToken: string): Promise<void> {
  try {
    const body = new ObjectiveTagDTO(tag.tagId, objectiveId);

    await http.post(`/tags/objective?add=${false}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddTagToObjective(tag: Tag, objectiveId: number, accessToken: string): Promise<void> {
  try {
    const body = new ObjectiveTagDTO(tag.tagId, objectiveId);
    await http.post<TagDTO>(`/tags/objective?add=${true}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//process
export async function apiRemoveTagFromProcess(tag: Tag, processId: number, accessToken: string): Promise<void> {
  try {
    const body = new ProcessTagDTO(tag.tagId, processId);
    await http.post(`/tags/process?add=${false}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddTagToProcess(tag: Tag, processId: number, accessToken: string): Promise<void> {
  try {
    const body = new ProcessTagDTO(tag.tagId, processId);
    await http.post<TagDTO>(`/tags/process?add=${true}`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
